import React, { useContext, useEffect, useState } from 'react';
import { Flex, TabsProps } from 'antd';

import BilledTable from '@totem/components/ticketing/billing/billedTable';
import BilledUnbilledSummary from '@totem/components/ticketing/billing/billedUnbilledSummary';
import BillingContainer from '@totem/components/ticketing/billing/billingContainer';
import ButtonCreateBilling from '@totem/components/ticketing/billing/buttonCreateBilling';
import UnbilledSelectedCounts from '@totem/components/ticketing/billing/unbilledSelectedCounts';
import UnbilledTable from '@totem/components/ticketing/billing/unbilledTable';
import TicketContext from '@totem/components/ticketing/details/ticketContext';
import BulkTimeEntryLayout from '@totem/components/ticketing/details/timeEntry/bulkTimeEntry/bulkEntryLayout';
import TimeEntryTable from '@totem/components/ticketing/details/timeEntry/timeEntriesTable';
import TimeEntryDialog from '@totem/components/ticketing/details/timeEntry/timeEntryDialog';
import TimeEntryEditDialog from '@totem/components/ticketing/details/timeEntry/timeEntryEditDialog';
import TimeEntryHoursSummary from '@totem/components/ticketing/details/timeEntry/timeEntryHoursSummary';
import { Resources, Tasks } from '@totem/components/ticketing/ticketing';
import IBTabs from '@totem/components/ui_components/antDesignOverrides/IBTabs';
import { isNotNull } from '@totem/utilities/common';
import {
  sortNumberAscending,
  sortStringAscending,
} from '@totem/utilities/tableUtilities';

import '../../ticketing.css';

type Props = {
  selectedTab: string;
  onTabSelect: (newTab: string) => void;
};

const TimeTracking = ({ selectedTab, onTabSelect }: Props) => {
  const { ticket, action } = useContext(TicketContext);
  const [resources, setResources] = useState<Resources>([]);
  const [tasks, setTasks] = useState<Tasks>([]);
  const [localSelectedTab, setLocalSelectedTab] = useState<string>(selectedTab);
  const [refreshCount, setRefreshCount] = useState<number>(0);

  useEffect(() => {
    if (
      isNotNull(ticket) &&
      isNotNull(ticket.ticket) &&
      isNotNull(ticket.ticket.partiesInvolved)
    ) {
      const res = ticket.ticket.partiesInvolved
        .map((pi) => {
          return {
            id: pi.id,
            name: pi.name,
            email: pi.email,
          };
        })
        .sort((compA, compB) => sortStringAscending(compA.name, compB.name));
      setResources(res);
    } else {
      setResources([]);
    }
  }, [ticket]);

  useEffect(() => {
    if (
      isNotNull(ticket) &&
      isNotNull(ticket.ticket) &&
      isNotNull(ticket.ticket.tasks)
    ) {
      const tsks = ticket.ticket.tasks
        .filter((chk) => !chk.isDeleted)
        .sort((compA, compB) =>
          sortNumberAscending(compA.number, compB.number),
        );
      setTasks(tsks);
    } else {
      setTasks([]);
    }
  }, [ticket]);

  useEffect(() => {
    setRefreshCount((prev) => prev + 1);
  }, [ticket.ticket.timeEntries]);

  const getTabItems = () => {
    const tabItems: TabsProps['items'] = [
      {
        key: 'gridEntry',
        label: 'Grid Time Entry',
        children: <BulkTimeEntryLayout />,
      },
      {
        key: 'report',
        label: 'Time Entered',
        children: (
          <TimeEntryTable
            timeEntries={
              isNotNull(ticket) &&
              isNotNull(ticket.ticket) &&
              isNotNull(ticket.ticket.timeEntries)
                ? ticket.ticket.timeEntries
                : []
            }
          />
        ),
      },
    ];

    if (isNotNull(ticket) && isNotNull(ticket.ticket)) {
      tabItems.push({
        key: 'billableHours_unbilled',
        label: 'Billable (Unbilled)',
        children: <UnbilledTable />,
      });

      tabItems.push({
        key: 'billableHours_billed',
        label: 'Billable (Billed)',
        children: <BilledTable />,
      });
    }

    return tabItems;
  };

  const handleTabChanged = (newTab: string) => {
    setLocalSelectedTab(newTab);

    onTabSelect(newTab);
  };

  return (
    <>
      <BillingContainer
        billingParams={{
          resourceEmails: [],
          ticketIds: [ticket.ticket.id],
        }}
        refreshCount={refreshCount}
      >
        <IBTabs
          defaultActiveKey={selectedTab}
          items={getTabItems()}
          onChange={(newKey) => handleTabChanged(newKey)}
          tabBarExtraContent={
            localSelectedTab === 'billableHours_unbilled' ? (
              <Flex justify={'flex-end'} align={'center'} gap={24}>
                <BilledUnbilledSummary />
                <span style={{ color: '#ff0000' }}>
                  <UnbilledSelectedCounts />
                </span>
                <ButtonCreateBilling />
              </Flex>
            ) : localSelectedTab === 'billableHours_billed' ? (
              <BilledUnbilledSummary />
            ) : localSelectedTab === 'report' ? (
              <TimeEntryHoursSummary />
            ) : null
          }
        />
      </BillingContainer>
      {action === 'show-time-entry-create' &&
        isNotNull(ticket) &&
        isNotNull(ticket.ticket) && (
          <TimeEntryDialog
            resources={resources}
            tasks={isNotNull(tasks) ? tasks : []}
          />
        )}
      {action === 'show-time-entry-edit' &&
        isNotNull(ticket) &&
        isNotNull(ticket.ticket) && (
          <TimeEntryEditDialog
            resources={resources}
            tasks={isNotNull(tasks) ? tasks : []}
          />
        )}
    </>
  );
};

export default TimeTracking;
